import React from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  CheckCircle2,
  LoaderCircle,
  MapPin,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";

import {
  createSollioDeliveryAddressRule,
  deleteSollioDeliveryAddressRule,
  fetchSollioDeliveryAddressRules,
  updateSollioDeliveryAddressRule,
} from "../lib/sollioDeliveryAddressApi.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  buildSollioRuleAliasesForUpdate,
  normalizeSollioRuleName,
} from "../utils/sollioDeliveryAddressRule.js";

const NEW_RULE_VALUE = "__new__";
const PRIMARY_BUTTON_CLASS_NAME = "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-3 py-2 text-[12px] font-semibold text-white transition hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";
const FIELD_CLASS_NAME = "min-h-9 w-full rounded-[4px] border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] text-slate-800 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";

function resolveMessage(error, fallback) {
  return typeof error?.message === "string" && error.message.trim() ? error.message.trim() : fallback;
}

function addressHeading(option) {
  const clientCode = typeof option?.client_code === "string" && option.client_code.trim()
    ? option.client_code.trim()
    : "Sans code";
  const correspondenceName = option?.correspondence_name || option?.client_name || "Correspondance sans nom";
  return `${clientCode} · ${correspondenceName}`;
}

function searchableAddressText(option) {
  return `${option?.client_code || ""} ${option?.client_name || ""} ${option?.correspondence_name || ""} ${option?.address || ""}`
    .toLocaleLowerCase("fr-CA");
}

export default function SollioDeliveryAddressManagerDialog({
  canManageRules = false,
  isOpen = false,
  onClose,
}) {
  const closeButtonRef = React.useRef(null);
  const deleteCancelButtonRef = React.useRef(null);
  const titleId = React.useId();
  const descriptionId = React.useId();
  const [registry, setRegistry] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [selectedRuleId, setSelectedRuleId] = React.useState(NEW_RULE_VALUE);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [selectedAddressIds, setSelectedAddressIds] = React.useState([]);
  const [addressSearch, setAddressSearch] = React.useState("");

  const rules = Array.isArray(registry?.rules) ? registry.rules : [];
  const availableCorrespondences = Array.isArray(registry?.available_correspondences)
    ? registry.available_correspondences
    : [];
  const selectedRule = rules.find((rule) => String(rule.id) === selectedRuleId) || null;
  const correspondenceById = React.useMemo(() => {
    const result = new Map();
    [...availableCorrespondences, ...(Array.isArray(selectedRule?.addresses) ? selectedRule.addresses : [])]
      .forEach((option) => result.set(String(option.correspondence_id), option));
    return result;
  }, [availableCorrespondences, selectedRule]);
  const selectedAddresses = selectedAddressIds
    .map((correspondenceId) => correspondenceById.get(String(correspondenceId)))
    .filter(Boolean);
  const normalizedAddressSearch = addressSearch.trim().toLocaleLowerCase("fr-CA");
  const searchResults = normalizedAddressSearch
    ? availableCorrespondences
      .filter((option) => !selectedAddressIds.includes(String(option.correspondence_id)))
      .filter((option) => searchableAddressText(option).includes(normalizedAddressSearch))
      .slice(0, 8)
    : [];

  const loadRegistry = React.useCallback(async (preferredRuleId = "") => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const payload = await fetchSollioDeliveryAddressRules();
      const nextRules = Array.isArray(payload?.rules) ? payload.rules : [];
      setRegistry({
        available_correspondences: Array.isArray(payload?.available_correspondences)
          ? payload.available_correspondences
          : [],
        rules: nextRules,
      });
      setSelectedRuleId((currentRuleId) => {
        const candidate = preferredRuleId || currentRuleId;
        return nextRules.some((rule) => String(rule.id) === candidate)
          ? candidate
          : NEW_RULE_VALUE;
      });
      return true;
    } catch (error) {
      setRegistry(null);
      setErrorMessage(resolveMessage(error, "Impossible de charger les correspondances Sollio."));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setIsDeleteConfirmationOpen(false);
      return;
    }
    setSuccessMessage("");
    void loadRegistry();
  }, [isOpen, loadRegistry]);

  React.useEffect(() => {
    if (!selectedRule) {
      setName("");
      setSelectedAddressIds([]);
      setAddressSearch("");
      return;
    }
    setName(selectedRule.label || "");
    setSelectedAddressIds(
      Array.isArray(selectedRule.addresses)
        ? selectedRule.addresses.map((option) => String(option.correspondence_id))
        : [],
    );
    setAddressSearch("");
  }, [selectedRule]);

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (isDeleteConfirmationOpen && !isSaving) {
          setIsDeleteConfirmationOpen(false);
          return;
        }
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDeleteConfirmationOpen, isOpen, isSaving, onClose]);

  React.useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      return undefined;
    }
    const frameId = window.requestAnimationFrame?.(() => closeButtonRef.current?.focus());
    return () => {
      if (frameId != null) {
        window.cancelAnimationFrame?.(frameId);
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isDeleteConfirmationOpen || typeof window === "undefined") {
      return undefined;
    }
    const frameId = window.requestAnimationFrame?.(() => deleteCancelButtonRef.current?.focus());
    return () => {
      if (frameId != null) {
        window.cancelAnimationFrame?.(frameId);
      }
    };
  }, [isDeleteConfirmationOpen]);

  const handleRuleSelectionChange = React.useCallback((ruleId) => {
    setIsDeleteConfirmationOpen(false);
    setSelectedRuleId(String(ruleId));
    setErrorMessage("");
    setSuccessMessage("");
  }, []);

  const handleAddAddress = React.useCallback((correspondenceId) => {
    const normalizedId = String(correspondenceId);
    setSelectedAddressIds((currentIds) => (
      currentIds.includes(normalizedId) ? currentIds : [...currentIds, normalizedId]
    ));
    setErrorMessage("");
    setSuccessMessage("");
  }, []);

  const handleRemoveAddress = React.useCallback((correspondenceId) => {
    const normalizedId = String(correspondenceId);
    setSelectedAddressIds((currentIds) => currentIds.filter((item) => item !== normalizedId));
    setErrorMessage("");
    setSuccessMessage("");
  }, []);

  const handleSave = React.useCallback(async () => {
    const normalizedName = name.trim();
    if (!normalizedName || !normalizeSollioRuleName(normalizedName) || selectedAddressIds.length === 0) {
      setErrorMessage("Saisissez la destination et au moins une adresse.");
      return;
    }
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const payload = {
        aliases: selectedRule
          ? buildSollioRuleAliasesForUpdate(selectedRule, normalizedName)
          : [normalizedName],
        correspondence_ids: selectedAddressIds,
        label: normalizedName,
      };
      const savedRule = selectedRule
        ? await updateSollioDeliveryAddressRule(selectedRule.id, {
          ...payload,
          expected_updated_at: selectedRule.updated_at,
        })
        : await createSollioDeliveryAddressRule(payload);
      const didReload = await loadRegistry(String(savedRule?.id || selectedRule?.id || ""));
      if (didReload) {
        setSuccessMessage(selectedRule ? "Correspondance Sollio mise à jour." : "Correspondance Sollio créée.");
      }
    } catch (error) {
      setErrorMessage(resolveMessage(error, "Impossible d’enregistrer la correspondance Sollio."));
    } finally {
      setIsSaving(false);
    }
  }, [loadRegistry, name, selectedAddressIds, selectedRule]);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!selectedRule?.id) {
      return;
    }
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await deleteSollioDeliveryAddressRule(selectedRule.id);
      const didReload = await loadRegistry(NEW_RULE_VALUE);
      if (didReload) {
        setSuccessMessage("Correspondance Sollio supprimée.");
      }
    } catch (error) {
      setErrorMessage(resolveMessage(error, "Impossible de supprimer la correspondance Sollio."));
    } finally {
      setIsDeleteConfirmationOpen(false);
      setIsSaving(false);
    }
  }, [loadRegistry, selectedRule]);

  if (!isOpen) {
    return null;
  }

  const canSave = canManageRules
    && !isSaving
    && Boolean(normalizeSollioRuleName(name))
    && selectedAddressIds.length > 0;
  const content = (
    <div className="fixed inset-0 z-modal isolate" data-sollio-delivery-address-manager="true">
      <div
        className="absolute inset-0 overflow-y-auto bg-slate-950/45 p-3"
        onMouseDown={(event) => {
          if (!event.target?.closest?.('[role="dialog"]')) {
            onClose?.();
          }
        }}
      >
        <div className="flex min-h-full items-center justify-center">
          <section
            aria-describedby={descriptionId}
            aria-labelledby={titleId}
            aria-modal="true"
            className="relative grid max-h-[calc(100dvh-1.5rem)] w-full max-w-[68rem] grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-[4px] border border-black/10 bg-white shadow-[0_28px_64px_rgba(15,23,42,0.2)]"
            role="dialog"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-shell-action-soft-ink)]">Import Sollio</p>
                <h2 id={titleId} className="mt-0.5 text-[1.1rem] font-semibold text-slate-950">Gérer les clients à adresses variables</h2>
                <p id={descriptionId} className="mt-1 text-[11px] leading-4 text-slate-600">
                  Associez une destination Sollio aux adresses permises.
                </p>
              </div>
              <button
                ref={closeButtonRef}
                aria-label="Fermer le gestionnaire Sollio"
                className={PRIMARY_BUTTON_CLASS_NAME}
                onClick={() => onClose?.()}
                type="button"
              >
                <X size={14} aria-hidden="true" /> Fermer
              </button>
            </header>

            <div className="min-h-0 overflow-y-auto p-4">
              {errorMessage ? (
                <div className="mb-3 flex items-start gap-2 rounded-[4px] border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-4 text-amber-950">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{errorMessage}</span>
                </div>
              ) : null}
              {successMessage ? (
                <div className="mb-3 flex items-start gap-2 rounded-[4px] border border-[var(--color-border-success-subtle)] bg-accent-soft px-3 py-2 text-[11px] leading-4 text-[var(--color-shell-action-soft-ink)]">
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{successMessage}</span>
                </div>
              ) : null}

              {isLoading && !registry ? (
                <div className="flex min-h-40 items-center justify-center gap-2 text-[12px] text-slate-600">
                  <LoaderCircle size={16} className="animate-spin" aria-hidden="true" /> Chargement des correspondances…
                </div>
              ) : null}

              {registry ? (
                <div
                  className="grid min-h-0 gap-3 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start"
                  data-sollio-rule-layout="sidebar"
                >
                  <section
                    className="min-w-0 rounded-[4px] border border-slate-200 bg-slate-50 p-2.5 md:sticky md:top-0"
                    data-sollio-rule-selector="true"
                  >
                    <h3 className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Clients déjà configurés</h3>
                    <div
                      aria-label="Clients déjà configurés"
                      className="mt-2 grid max-h-40 gap-1.5 overflow-y-auto overscroll-y-contain pr-1 md:max-h-[28rem]"
                      role="region"
                    >
                      <button
                        aria-label="Nouvelle correspondance"
                        aria-pressed={selectedRuleId === NEW_RULE_VALUE}
                        className={`inline-flex min-h-9 w-full items-center gap-1.5 rounded-[4px] border px-2.5 py-2 text-left text-[11px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                          selectedRuleId === NEW_RULE_VALUE
                            ? "border-accent bg-accent text-white shadow-sm"
                            : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                        data-sollio-rule-option={NEW_RULE_VALUE}
                        disabled={isSaving}
                        onClick={() => handleRuleSelectionChange(NEW_RULE_VALUE)}
                        type="button"
                      >
                        <Plus size={13} className="shrink-0" aria-hidden="true" /> Nouvelle
                      </button>
                      {rules.map((rule) => {
                        const ruleId = String(rule.id);
                        const isSelected = selectedRuleId === ruleId;
                        const addressCount = Array.isArray(rule.addresses) ? rule.addresses.length : 0;
                        return (
                          <button
                            aria-pressed={isSelected}
                            className={`inline-flex min-h-9 w-full min-w-0 items-center justify-between gap-2 rounded-[4px] border px-2.5 py-2 text-left text-[11px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                              isSelected
                                ? "border-accent bg-accent text-white shadow-sm"
                                : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
                            }`}
                            data-sollio-rule-option={ruleId}
                            disabled={isSaving}
                            key={rule.id}
                            onClick={() => handleRuleSelectionChange(ruleId)}
                            title={`${rule.label} · ${addressCount} adresse${addressCount > 1 ? "s" : ""}`}
                            type="button"
                          >
                            <span className="min-w-0 flex-1 truncate">{rule.label}</span>
                            <span className={`shrink-0 rounded-[4px] border px-1.5 py-0.5 text-[9px] font-semibold ${
                              isSelected
                                ? "border-white/35 bg-white/15 text-white"
                                : "border-slate-200 bg-slate-50 text-slate-600"
                            }`}>
                              {addressCount}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="rounded-[4px] border border-slate-200 bg-white p-3">
                    <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                      Destination Sollio
                      <input
                        className={FIELD_CLASS_NAME}
                        disabled={!canManageRules || isSaving}
                        onChange={(event) => {
                          setName(event.target.value);
                          setErrorMessage("");
                          setSuccessMessage("");
                        }}
                        placeholder="Ex. FERME C.D. SAUVÉ 2000"
                        value={name}
                      />
                    </label>
                    <p className="mt-1 text-[10.5px] leading-4 text-slate-500">
                      Écrivez la « Destination » exacte du fichier Sollio.
                    </p>

                    <h3 className="mt-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-600">Adresses associées</h3>

                    {selectedAddresses.length === 0 ? (
                      <div className="mt-2 flex items-start gap-2 rounded-[4px] border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-4 text-amber-950">
                        <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                        <span>
                          {selectedRule
                            ? "Cette correspondance ne possède plus d’adresse valide. Recherchez et ajoutez une adresse du Data Manager."
                            : "Recherchez et ajoutez au moins une adresse du Data Manager."}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 grid gap-1.5 md:grid-cols-2">
                        {selectedAddresses.map((option) => (
                          <article className="flex min-w-0 items-start gap-2 rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-2" key={option.correspondence_id}>
                            <MapPin size={15} className="mt-0.5 shrink-0 text-accent" aria-hidden="true" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[11px] font-semibold text-slate-900" title={addressHeading(option)}>{addressHeading(option)}</p>
                              <p className="mt-0.5 text-[10px] leading-3.5 text-slate-500">{option.address || "Adresse non disponible"}</p>
                            </div>
                            <button
                              aria-label={`Retirer ${addressHeading(option)}`}
                              className="inline-flex min-h-7 shrink-0 items-center justify-center rounded-[4px] border border-slate-200 bg-white px-2 text-[10.5px] font-semibold text-slate-600 transition hover:border-red-200 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={!canManageRules || isSaving}
                              onClick={() => handleRemoveAddress(option.correspondence_id)}
                              type="button"
                            >
                              <X size={13} aria-hidden="true" /> Retirer
                            </button>
                          </article>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 rounded-[4px] border border-slate-200 bg-slate-50 p-2.5">
                      <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                        Ajouter une adresse du Data Manager
                        <span className="relative block">
                          <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                          <input
                            aria-label="Rechercher une adresse du Data Manager"
                            className={`${FIELD_CLASS_NAME} pl-8`}
                            disabled={!canManageRules || isSaving}
                            onChange={(event) => setAddressSearch(event.target.value)}
                            placeholder="Rechercher par code, nom ou adresse"
                            value={addressSearch}
                          />
                        </span>
                      </label>

                      {normalizedAddressSearch ? (
                        <div className="mt-2 grid gap-1">
                          {searchResults.length > 0 ? searchResults.map((option) => (
                            <button
                              className="flex min-w-0 items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-2.5 py-2 text-left transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={!canManageRules || isSaving}
                              key={option.correspondence_id}
                              onClick={() => handleAddAddress(option.correspondence_id)}
                              type="button"
                            >
                              <Plus size={14} className="shrink-0 text-accent" aria-hidden="true" />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-[11px] font-semibold text-slate-900" title={addressHeading(option)}>{addressHeading(option)}</span>
                                <span className="block truncate text-[10px] text-slate-500" title={option.address}>{option.address}</span>
                              </span>
                              <span className="text-[10px] font-semibold text-slate-700">Ajouter</span>
                            </button>
                          )) : (
                            <p className="rounded-[4px] border border-slate-200 bg-white px-2.5 py-2 text-[11px] text-slate-500">Aucune adresse correspondante trouvée.</p>
                          )}
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                        {selectedRule ? (
                          <button
                            className="inline-flex min-h-9 items-center gap-1.5 rounded-[4px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-700 transition hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                            disabled={!canManageRules || isSaving}
                            onClick={() => setIsDeleteConfirmationOpen(true)}
                            type="button"
                          >
                            <Trash2 size={14} aria-hidden="true" /> Supprimer
                          </button>
                        ) : null}
                        <button className={PRIMARY_BUTTON_CLASS_NAME} disabled={!canSave} onClick={handleSave} type="button">
                          {isSaving ? <LoaderCircle size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
                          {isSaving ? "Enregistrement…" : selectedRule ? "Mettre à jour" : "Créer"}
                        </button>
                    </div>
                  </section>
                </div>
              ) : null}
            </div>

            {isDeleteConfirmationOpen && selectedRule ? (
              <div
                className="absolute inset-0 z-[1] flex items-center justify-center bg-slate-950/45 p-4"
                data-sollio-rule-delete-confirmation="true"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget && !isSaving) {
                    setIsDeleteConfirmationOpen(false);
                  }
                }}
                role="presentation"
              >
                <section
                  aria-describedby={`${titleId}-delete-description`}
                  aria-labelledby={`${titleId}-delete-title`}
                  aria-modal="true"
                  className="w-full max-w-[28rem] overflow-hidden rounded-[4px] border border-red-200 bg-white shadow-[0_24px_56px_rgba(15,23,42,0.24)]"
                  role="alertdialog"
                >
                  <div className="h-1 w-full bg-red-600" />
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border border-red-200 bg-red-50 text-red-700">
                        <Trash2 size={17} aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-red-700">Confirmation</p>
                        <h3 id={`${titleId}-delete-title`} className="mt-1 text-[1rem] font-semibold text-slate-950">Supprimer cette correspondance ?</h3>
                        <p id={`${titleId}-delete-description`} className="mt-1.5 text-[11px] leading-4 text-slate-600">
                          La correspondance « {selectedRule.label} » sera supprimée avec tous ses choix d’adresse mémorisés. Cette action ne peut pas être annulée.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      <button
                        ref={deleteCancelButtonRef}
                        className="inline-flex min-h-9 items-center justify-center rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isSaving}
                        onClick={() => setIsDeleteConfirmationOpen(false)}
                        type="button"
                      >
                        Annuler
                      </button>
                      <button
                        className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-[4px] border border-red-600 bg-red-600 px-3 py-2 text-[12px] font-semibold text-white transition hover:border-red-700 hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isSaving}
                        onClick={handleConfirmDelete}
                        type="button"
                      >
                        {isSaving ? <LoaderCircle size={14} className="animate-spin" aria-hidden="true" /> : <Trash2 size={14} aria-hidden="true" />}
                        {isSaving ? "Suppression…" : "Supprimer"}
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );

  const portalRoot = getReactPortalRoot();
  return portalRoot && typeof HTMLElement !== "undefined" && portalRoot instanceof HTMLElement
    ? createPortal(content, portalRoot)
    : content;
}
